import Alert from 'common/components/alert/Alert';
import AlertType from 'common/types/AlertType';
import ApplicationTableContainer from 'domain/components/application/ApplicationTableContainer';
import { Application } from 'domain/types/Application';
import { Col, Row } from 'react-bootstrap';
import ActionCard from './ActionCard';
import AddRegistrationNumber from './AddRegistrationNumberContainer';
import MyRegistrationsTable from '../registration/MyRegistrationsTable';
import RejectionTypeCd from 'domain/enums/RejectionTypeCd';
import { isRejected } from './suggested-actions/rejections';

interface IAccountDashboardPresenter {
  name: string;
  hasMailingAddress: boolean;
  hasUploadedIdentity: boolean;
  hasRejectedShuol: boolean;
  hasRejectedIdentity: boolean;
  primaryResDeniedApps: Application[]; // used to route user to application with invalid proof of residence
  onNavigate: (to: string) => void;
  message?: { text: string; type: AlertType };
}

const AccountDashboardPresenter = (props: IAccountDashboardPresenter) => {
  const profileComplete = props.hasMailingAddress && props.hasUploadedIdentity;

  return (
    <Row>
      <Col className='col-md-11 offset-md-1'>
        <h2>Welcome to your dashboard, {props.name}!</h2>
        <p>
          <b>
            To modify your platform or room selections, simply open your
            application.
          </b>
        </p>
        {props.message && (
          <Alert type={props.message.type}>{props.message.text}</Alert>
        )}
        {!profileComplete && (
          <p>
            Now that you have created a login, we need you to complete your
            profile. Once this is complete, you will be able to start the
            application process.
          </p>
        )}
        <div className='row'>
          {!props.hasUploadedIdentity && (
            <ActionCard
              onClick={() => props.onNavigate('/profile#identity-verification')}
              title='Verify your identity'
              subtitle='Click here to upload identity documents.'
              description={
                <p>
                  All shared housing host applicants must provide proof of
                  individual identity.
                </p>
              }
            />
          )}
          {!props.hasMailingAddress && (
            <ActionCard
              onClick={() => props.onNavigate('/profile#contact-information')}
              title='Enter mailing address'
              subtitle='Click here to enter your mailing address.'
              description='We will use this address to mail important notices about your account.'
            />
          )}
          {props.hasRejectedIdentity && (
            <ActionCard
              onClick={() => props.onNavigate('/profile#identity-verification')}
              title='Re-verify your identity'
              subtitle='Click here to re-upload identity documents.'
              description={
                <p>
                  We have encountered an issue with your identity verification
                  document(s). Please review the Action Required below.
                </p>
              }
            />
          )}
          {props.primaryResDeniedApps &&
            // only list applications that have been actively rejected
            props.primaryResDeniedApps.map(
              (app, idx) =>
                Boolean(
                  isRejected(app, RejectionTypeCd.PRIMARY_RESIDENCE) ||
                    isRejected(app, RejectionTypeCd.PRIMARY_RESIDENCE_02)
                ) && (
                  <ActionCard
                    key={idx}
                    onClick={() =>
                      props.onNavigate(`/application-group/${app.groupRefKey}`)
                    }
                    title={`Proof of primary residency at ${app.addressLineOne} denied`}
                    titleSize='sm'
                    subtitle='Click here to re-upload proof of primary residency documents.'
                    description={
                      <p>
                        We have encountered an issue with your proof of primary
                        residency document(s). Please review the Action Required
                        below.
                      </p>
                    }
                  />
                )
            )}
          {props.hasRejectedShuol && (
            <ActionCard
              onClick={() => props.onNavigate('/profile#shuol')}
              title="Add Shared Housing Unit Operator's License"
              subtitle='Click here to add your SHUOL.'
              description={
                <p>
                  An application was rejected due to a missing SHUOL. Please add
                  your valid operator's license number and resubmit.
                </p>
              }
            />
          )}
        </div>
        <ApplicationTableContainer hideOnEmpty />
        <h3 className='mt-5'>My Registration Numbers</h3>
        <MyRegistrationsTable hideOnEmpty={true} />
        <AddRegistrationNumber />
      </Col>
    </Row>
  );
};

export default AccountDashboardPresenter;
